/**
 * Web Audio API player for real-time audio streaming
 */
export class AudioStreamPlayer {
  constructor() {
    this.audioContext = null;
    this.scriptProcessor = null;
    this.sampleQueue = [];
    this.currentChunk = null;
    this.readIndex = 0;
    this.isPlaying = false;
    this.gainBoost = 2.6;
    this.noiseGate = 0.008;
  }

  /**
   * Initialize audio context and script processor
   */
  initialize() {
    if (this.audioContext) return;

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioContext = this.audioContext;

    // Create script processor for real-time playback
    this.scriptProcessor = audioContext.createScriptProcessor(2048, 0, 1);
    
    this.scriptProcessor.onaudioprocess = (event) => {
      const outputData = event.outputBuffer.getChannelData(0);
      
      // Fill output buffer from queued chunks.
      for (let i = 0; i < outputData.length; i++) {
        if (!this.currentChunk || this.readIndex >= this.currentChunk.length) {
          this.currentChunk = this.sampleQueue.shift() || null;
          this.readIndex = 0;
        }

        if (this.currentChunk && this.readIndex < this.currentChunk.length) {
          const raw = this.currentChunk[this.readIndex++];
          const gated = Math.abs(raw) < this.noiseGate ? 0 : raw;
          const boosted = gated * this.gainBoost;
          outputData[i] = Math.max(-1, Math.min(1, boosted));
        } else {
          outputData[i] = 0;
        }
      }
    };

    // Connect to audio output
    this.scriptProcessor.connect(audioContext.destination);
    this.isPlaying = true;

    console.log('🎤 Audio context initialized');
  }

  /**
   * Queue audio samples for playback
   */
  play(samples) {
    if (!this.audioContext) {
      this.initialize();
    }

    // Keep latency low by dropping stale queued chunks when congested.
    if (this.sampleQueue.length > 20) {
      this.sampleQueue.splice(0, this.sampleQueue.length - 8);
    }

    this.sampleQueue.push(samples.slice());
  }

  /**
   * Stop playback and cleanup
   */
  stop() {
    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.sampleQueue = [];
    this.currentChunk = null;
    this.readIndex = 0;
    this.isPlaying = false;
    console.log('🎤 Audio playback stopped');
  }

  /**
   * Check if audio is currently playing
   */
  get playing() {
    return this.isPlaying && this.audioContext?.state === 'running';
  }
}
